import React, { useEffect, useState } from 'react';
import { InventoryItem } from './InventoryPage';
import { History, Calendar, User, ArrowDownRight, Loader2 } from 'lucide-react';
import { inventoryApi } from '../../../services/inventoryVendorApi';

interface InventoryHistoryProps {
    item: InventoryItem;
}

interface HistoryEvent {
    id: number;
    created_at: string; // Backend usually returns created_at or date
    date?: string; // Fallback
    user_name: string; // "user id and name" -> assume backend resolves to name or we might get user_id
    user_id?: number;
    action_type: string; // 'maintenance' | 'replacement' | 'new_install'
    quantity: number;
    notes?: string;
    unit?: string;
}

const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;

    const day = String(date.getDate()).padStart(2, '0');
    const month = date.toLocaleString('en-IN', { month: 'short' });
    const year = String(date.getFullYear()).slice(-2);

    return `${day}${month}${year}`;
};

const InventoryHistory: React.FC<InventoryHistoryProps> = ({ item }) => {
    const [history, setHistory] = useState<HistoryEvent[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchHistory = async () => {
            try {
                setLoading(true);
                // Assuming site code is handled by the API service internally or via context/localStorage
                // if needed passed as prop, but API service gets it from localStorage usually
                const response = await inventoryApi.getHistory(item.id);
                if (response.ok && response.data) {
                    // Adapt the response data to our frontend model if necessary
                    // This assumes the backend returns an array of history events
                    setHistory(response.data);
                } else {
setError('Failed to load history');
                    setHistory([]);
                }
            } catch (err) {
                console.error('Error fetching inventory history:', err);
                setError('Failed to load history');
            } finally {
                setLoading(false);
            }
        };

        if (item.id) {
            fetchHistory();
        }
    }, [item.id, item.last_updated]);

    const handleExport = () => {
        if (!history || history.length === 0) return;

        // Define headers: Usage History first, then Item Details
        const headers = [
            'Date',
            'Action',
            'User',
            'Notes',
            'Quantity',
            'EQUIPMENT NAME',
            'CATEGORY',
            'DESCRIPTION',
            'PRODUCT SERIAL NUMBER',
            'ELECTRICAL RATING',
            'CURRENT RATING',
            'VOLTAGE RATING',
            'POWER RATING',
            'REACTIVE POWER RATING',
            'APPARENT POWER RATING',
            'FREQUENCY RATING',
            'Capacity Amp/KW/KVA',
            'Make',
            'DataSheet',
            'InstalledQuantity',
            'SpareAvailability',
            'ConsumptionPattern_LastThreeMonths',
            'ConsumptionPattern_LastSixMonths',
            'ConsumptionPattern_LastTwelveMonths'
        ];

        // Convert to CSV rows
        const rows = history.map(event => [
            // History Details First
            new Date(event.created_at || event.date || '').toLocaleDateString('en-GB'),
            event.action_type || '-',
            event.user_name || '-',
            `"${(event.notes || '').replace(/"/g, '""')}"`,
            event.quantity,
            // Item Details Second
            `"${(item.equipment_name || '').replace(/"/g, '""')}"`,
            `"${(item.category || '').replace(/"/g, '""')}"`,
            `"${(item.description || '').replace(/"/g, '""')}"`,
            `"${(item.product_serial_number || '').replace(/"/g, '""')}"`,
            `"${(item.electrical_rating || '').replace(/"/g, '""')}"`,
            `"${(item.current_rating || '').replace(/"/g, '""')}"`,
            `"${(item.voltage_rating || '').replace(/"/g, '""')}"`,
            `"${(item.power_rating || '').replace(/"/g, '""')}"`,
            `"${(item.reactive_power_rating || '').replace(/"/g, '""')}"`,
            `"${(item.apparent_power_rating || '').replace(/"/g, '""')}"`,
            `"${(item.frequency_rating || '').replace(/"/g, '""')}"`,
            `"${(item.capacity_amp_kw_kva || '').replace(/"/g, '""')}"`,
            `"${(item.make || '').replace(/"/g, '""')}"`,
            `"${(item.datasheet || '').replace(/"/g, '""')}"`,
            item.installed_quantity || 0,
            item.spare_availability || 0,
            item.consumption_pattern_last_three_months || 0,
            item.consumption_pattern_last_six_months || 0,
            item.consumption_pattern_last_twelve_months || 0
        ]);

        // Combine headers and rows
        const csvContent = [
            headers.join(','),
            ...rows.map(row => row.join(','))
        ].join('\n');

        // Create blob and download
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);

        link.setAttribute('href', url);
        link.setAttribute('download', `Inventory_Usage_Report_${item.item_code}_${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <div className="w-full bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden my-3 relative z-10 transition-all duration-300 ease-in-out">
            <div className="flex flex-col h-full w-full min-w-0">
                <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center bg-white">
                    <div className="flex items-center gap-2">
                        <History className="w-4 h-4 text-gray-400" />
                        <h3 className="text-sm" style={{ fontWeight: 400, color: '#000000' }}>Use History</h3>
                    </div>
                    <button
                        onClick={handleExport}
                        disabled={!history.length}
                        className={`text-xs font-medium ${!history.length ? 'text-gray-300 cursor-not-allowed' : 'text-[#0066A3] hover:text-[#1C4D88] hover:underline'}`}
                    >
                        Export Log
                    </button>
                </div>

                <div className="overflow-auto flex-1 p-0">
                    {loading ? (
                        <div className="flex items-center justify-center h-32 text-gray-400">
                            <Loader2 className="w-5 h-5 animate-spin mr-2" />
                            <span className="text-sm">Loading history...</span>
                        </div>
                    ) : (
                        <table className="w-full text-sm text-left table-fixed">
                            <thead className="bg-gray-100 text-xs uppercase border-b border-gray-200">
                                <tr>
                                    <th className="px-6 py-3 w-1/5 whitespace-nowrap text-center" style={{ fontWeight: 400, color: '#000000' }}>Date</th>
                                    <th className="px-6 py-3 w-1/5 whitespace-nowrap text-center" style={{ fontWeight: 400, color: '#000000' }}>Action</th>
                                    <th className="px-6 py-3 w-1/5 whitespace-nowrap text-center" style={{ fontWeight: 400, color: '#000000' }}>User</th>
                                    <th className="px-6 py-3 w-1/5 whitespace-nowrap text-center" style={{ fontWeight: 400, color: '#000000' }}>Note</th>
                                    <th className="px-6 py-3 w-1/5 whitespace-nowrap text-center" style={{ fontWeight: 400, color: '#000000' }}>Qty</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50">
                                {history.map((event) => (
                                    <tr key={event.id} className="hover:bg-[#F2F8FC]/20 transition-colors group">
                                        <td className="px-6 py-3 text-gray-600 font-medium whitespace-nowrap text-xs text-center">
                                            <div className="flex items-center justify-center gap-2">
                                                <Calendar className="w-3 h-3 text-gray-300 group-hover:text-[#3EA3DA] transition-colors" />
                                                <span style={{ fontWeight: 400, color: '#000000' }}>{formatDate(event.created_at || event.date || '')}</span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-3 whitespace-nowrap text-center">
                                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium border uppercase tracking-wide ${event.action_type?.toLowerCase() === 'maintenance' ? 'bg-[#F2F8FC] text-[#1C4D88] border-[#E3F0F9]' :
                                                event.action_type?.toLowerCase() === 'replacement' ? 'bg-amber-50 text-amber-700 border-amber-100' :
                                                    event.action_type?.toLowerCase() === 'new_install' ? 'bg-emerald-50 text-emerald-700 border-emerald-100' :
                                                        'bg-slate-100 text-slate-700 border-slate-200'
                                                }`} style={{ fontWeight: 400, color: '#000000' }}>
                                                {event.action_type ? event.action_type.replace(/_/g, ' ') : '-'}
                                            </span>
                                        </td>
                                        <td className="px-6 py-3 text-gray-600 text-xs whitespace-nowrap text-center">
                                            <div className="flex items-center justify-center gap-1.5">
                                                <User className="w-3 h-3 text-gray-300" />
                                                <span style={{ fontWeight: 400, color: '#000000' }}>{event.user_name || '-'}</span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-3 text-gray-400 text-xs italic truncate text-center" title={event.notes}>
                                            <span style={{ fontWeight: 400, color: '#000000' }}>{event.notes || '-'}</span>
                                        </td>
                                        <td className="px-6 py-3 text-center whitespace-nowrap">
                                            <span className="flex items-center justify-center gap-1 text-xs" style={{ fontWeight: 400, color: '#000000' }}>
                                                {event.quantity}
                                                <ArrowDownRight className="w-3 h-3 text-red-400" />
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                                {history.length === 0 && !loading && (
                                    <tr>
                                        <td colSpan={5} className="py-8 text-center text-gray-400 text-sm">
                                            No recent usage history recorded
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </div >
    );
};

export default InventoryHistory;

